import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import impactSchoolLogo from '../assets/logos/impact_school_logo.jpg';
import sofatutorLogo from '../assets/logos/sofatutor_logo.jpg';
import dciLogo from '../assets/logos/dci_digital_career_institute_logo.jpg';
import linneLogo from '../assets/logos/linneuniversitetet_logo.jpg';
import codeUniLogo from '../assets/logos/code_university_logo.jpg';

const Hero = () => {
  const clientLogos = [
    { name: "DCI Digital Career Institute", logo: dciLogo },
    { name: "Impact School", logo: impactSchoolLogo },
    { name: "Sofatutor", logo: sofatutorLogo },
    { name: "CODE University", logo: codeUniLogo },
    { name: "Linneuniversitetet", logo: linneLogo },
  ];

  return (
    <HeroSection>
      <BackgroundPattern />

      <Container>
        <BadgeContainer>
          <StatusBadge
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <GreenDot />
            Currently onboarding select companies
          </StatusBadge>
        </BadgeContainer>

        <HeroContent>
          <MainHeading
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <motion.span
              initial={{ opacity: 0, filter: 'blur(10px)' }}
              animate={{ opacity: 1, filter: 'blur(0px)' }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="orange-text"
            >
              Never Miss a Lead Again
            </motion.span>
            <br />
            <motion.span
              initial={{ opacity: 0, filter: 'blur(10px)' }}
              animate={{ opacity: 1, filter: 'blur(0px)' }}
              transition={{ duration: 0.5, delay: 0.5 }}
            >
              AI Voice Agents That <Diamond>✦</Diamond>
            </motion.span>
            <br />
            <motion.span
              initial={{ opacity: 0, filter: 'blur(10px)' }}
              animate={{ opacity: 1, filter: 'blur(0px)' }}
              transition={{ duration: 0.5, delay: 0.7 }}
              className="orange-text"
            >
              Call, Qualify & Convert 24/7
            </motion.span>
          </MainHeading>

          <SubHeading
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.9 }}
          >
            AI-powered voice agents handle your calls
            <br />24/7—seamless, scalable, and efficient.
          </SubHeading>

          <ButtonContainer
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 1.1 }}
          >
            <BookCallButton href="https://cal.com/sonorai/intro" target="_blank" rel="noopener noreferrer">
              Book A Call <ArrowRight>→</ArrowRight>
            </BookCallButton>
          </ButtonContainer>

        </HeroContent>

        {/* Commented out for now - will be used later
        <ClientsSection
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 1.3 }}
        >
          <ClientsLabel>Our services are featured on</ClientsLabel>
          <LogoGrid>
            {clientLogos.map((client, index) => (
              <ClientLogo
                key={index}
                src={client.logo}
                alt={client.name}
                whileHover={{ scale: 1.05 }}
                transition={{ type: "spring", stiffness: 400, damping: 10 }}
              />
            ))}
          </LogoGrid>
        </ClientsSection>
        */}
      </Container>
    </HeroSection>
  );
};

const BackgroundPattern = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #000000;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image:
      radial-gradient(rgba(255, 255, 255, 0.05) 1px, transparent 1px);
    background-size: 16px 16px;
    opacity: 0.8;
  }
`;

const HeroSection = styled.section`
  padding: 150px 0 0;
  position: relative;
  overflow: hidden;
  min-height: 90vh;
  display: flex;
  flex-direction: column;
  align-items: center;

  @media (min-width: 992px) {
    padding: 180px 0 0;
  }
`;

const Container = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const BadgeContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  margin-bottom: 24px;
  margin-top: 32px;
`;

const StatusBadge = styled(motion.div)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(25, 25, 25, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 50px;
  padding: 8px 16px;
  gap: 8px;
  font-size: 14px;
  font-weight: 400;
  color: white;
  backdrop-filter: blur(5px);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
`;

const GreenDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #13C71A;
  position: relative;

  &:after {
    content: '';
    position: absolute;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background-color: rgba(19, 199, 26, 0.3);
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    animation: pulse 1.5s infinite;
  }

  @keyframes pulse {
    0% {
      transform: translate(-50%, -50%) scale(1);
      opacity: 1;
    }
    100% {
      transform: translate(-50%, -50%) scale(1.5);
      opacity: 0;
    }
  }
`;

const HeroContent = styled.div`
  text-align: center;
  max-width: 900px;
`;

const MainHeading = styled(motion.h1)`
  font-size: 48px;
  line-height: 1.2;
  margin-bottom: 16px;
  color: white;
  font-weight: 400;
  letter-spacing: -0.5px;
  font-family: var(--font-heading);

  .orange-text {
    color: var(--color-ui-orange);
    font-weight: 600;
  }

  @media (max-width: 768px) {
    font-size: 36px;
  }

  @media (max-width: 480px) {
    font-size: 28px;
  }
`;

const Diamond = styled.span`
  display: inline-block;
  font-size: 32px;
  vertical-align: middle;
  margin: 0 4px;
  position: relative;
  top: -3px;
`;

const SubHeading = styled(motion.p)`
  font-size: 24px;
  color: var(--color-text-gray);
  margin: 0 auto 48px;
  font-weight: 400;
  max-width: 600px;

  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const ButtonContainer = styled(motion.div)`
  margin-bottom: 50px;
`;

const BookCallButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background-color: #00A3FF;
  color: white;
  padding: 15px 30px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 16px;
  transition: all 0.3s ease;
  backdrop-filter: blur(5px);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 20px rgba(0, 168, 255, 0.3);
  }
`;

const ArrowRight = styled.span`
  display: inline-flex;
  font-size: 16px;
  transition: transform 0.3s ease;

  ${BookCallButton}:hover & {
    transform: translateX(3px);
  }
`;

const ClientsSection = styled(motion.div)`
  margin-top: 20px; /* Reduce top margin */
  margin-bottom: 0px; /* Remove extra space */
  width: 100%;
  text-align: center;
`;

const ClientsLabel = styled.p`
  font-size: 14px;
  color: var(--color-text-gray);
  margin-bottom: 30px;
  font-weight: 400;
`;

const LogoGrid = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 24px;
  flex-wrap: wrap;
`;

const ClientLogo = styled(motion.img)`
  height: 50px;
  opacity: 0.75;
  filter: none;
  transition: all 0.3s ease;
  flex-shrink: 0; /* Prevent shrinking in scrolling */

  &:hover {
    opacity: 1;
    filter: grayscale(0%);
  }

  @media (max-width: 768px) {
    height: 40px; /* Adjust size on mobile */
    margin-right: 20px; /* Add spacing for horizontal scroll */
  }
`;

export default Hero;
